import { useState } from "react";
import { Package, Pencil, Plus, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { CsvImportDialog } from "@/components/CsvImportDialog";
import { IngredientDialog } from "@/components/IngredientDialog";
import { EmptyState, PageHeader } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi } from "@/hooks/useApi";
import { api, brl, brlUnit, errMsg, num } from "@/lib/api";

export default function Ingredients() {
  const [q, setQ] = useState("");
  const { data, loading, reload } = useApi("/ingredients");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [csv, setCsv] = useState(false);
  const list = (data || []).filter((i) => i.name.toLowerCase().includes(q.toLowerCase()) || (i.supplier || "").toLowerCase().includes(q.toLowerCase()));

  const remove = async (id) => {
    try { await api.delete(`/ingredients/${id}`); toast.success("Insumo excluído"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader testId="ingredients-page" title="Insumos" subtitle="Sua matéria-prima. Quando o preço de um insumo muda, todas as fichas técnicas que o usam são recalculadas automaticamente."
        help="Insumo é tudo que você compra para produzir: carne, pão, embalagem. Informe quanto vem na embalagem e quanto ela custa (ex.: 700 g por R$ 10,00) — calculamos o preço por grama, ml ou unidade." helpTitle="O que é insumo?"
        actions={<>
          <Button variant="outline" onClick={() => setCsv(true)} data-testid="ingredients-import-btn"><Upload className="w-4 h-4 mr-2" />Importar CSV</Button>
          <Button onClick={() => setDlg({ open: true, item: null })} data-testid="ingredients-new-btn"><Plus className="w-4 h-4 mr-2" />Novo insumo</Button>
        </>} />

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="ingredients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou fornecedor..." className="pl-9" />
      </div>

      {!loading && !list.length ? (
        <EmptyState testId="ingredients-empty" icon={Package} title={q ? "Nenhum insumo encontrado" : "Nenhum insumo ainda"} text="Comece cadastrando os ingredientes que você mais usa. Pode importar de uma planilha em CSV."
          action={<Button onClick={() => setDlg({ open: true, item: null })} data-testid="ingredients-empty-new">Cadastrar primeiro insumo</Button>} />
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden rise">
          <Table>
            <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Embalagem</TableHead><TableHead className="text-right">Preço embalagem</TableHead><TableHead className="text-right">Preço por medida</TableHead><TableHead className="hidden md:table-cell">Fornecedor</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((i) => (
                <TableRow key={i.id} data-testid={`ingredient-row-${i.id}`}>
                  <TableCell className="font-medium">{i.name}{i.low_stock && <Badge className="ml-2 bg-rose-500/15 text-rose-500 border-0 text-[10px]" data-testid={`ingredient-low-${i.id}`}>Estoque baixo</Badge>}</TableCell>
                  <TableCell><Badge variant="secondary" className="num">{num(i.package_qty, 3)} {i.unit}</Badge></TableCell>
                  <TableCell className="text-right num">{brl(i.package_price)}</TableCell>
                  <TableCell className="text-right num text-primary" data-testid={`ingredient-unit-price-${i.id}`}>{brlUnit(i.price_per_unit)}<span className="text-muted-foreground text-xs">/{i.unit}</span></TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{i.supplier || "—"}</TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <Button variant="ghost" size="icon" data-testid={`ingredient-edit-${i.id}`} onClick={() => setDlg({ open: true, item: i })} aria-label="Editar"><Pencil className="w-4 h-4" /></Button>
                      <ConfirmDelete what={`o insumo "${i.name}"`} onConfirm={() => remove(i.id)} testId={`ingredient-delete-${i.id}`} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border" data-testid="ingredients-count">{list.length} insumo(s)</div>
        </div>
      )}

      <IngredientDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} ingredient={dlg.item} onSaved={reload} />
      <CsvImportDialog open={csv} onOpenChange={setCsv} onImported={reload} />
    </div>
  );
}
